import { useEffect, useRef, useCallback } from 'react';

interface AutoSaveOptions {
  saveFunction: () => Promise<void>;
  debounceMs?: number;
  enabled?: boolean;
}

/**
 * Custom hook for auto-saving cart data with debouncing
 * Saves automatically after user stops typing, on blur, visibility change, and before unload
 */
export function useAutoSaveCart({
  saveFunction,
  debounceMs = 2000, // 2 seconds default
  enabled = true,
}: AutoSaveOptions) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSaveRef = useRef<number>(0);
  const isSavingRef = useRef<boolean>(false);
  /** If a save was requested while one was in flight, run again after it finishes. */
  const pendingSaveRef = useRef<boolean>(false);
  const saveFunctionRef = useRef(saveFunction);
  saveFunctionRef.current = saveFunction;

  const runSave = useCallback(async () => {
    if (!enabled) return;

    if (isSavingRef.current) {
      pendingSaveRef.current = true;
      return;
    }

    const now = Date.now();
    if (now - lastSaveRef.current < 500) {
      pendingSaveRef.current = true;
      if (!timeoutRef.current) {
        timeoutRef.current = setTimeout(() => {
          timeoutRef.current = null;
          void runSave();
        }, 500);
      }
      return;
    }

    try {
      isSavingRef.current = true;
      pendingSaveRef.current = false;
      await saveFunctionRef.current();
      lastSaveRef.current = Date.now();
    } catch (error) {
      console.error('[AutoSave] Error saving cart:', error);
    } finally {
      isSavingRef.current = false;
      if (pendingSaveRef.current) {
        pendingSaveRef.current = false;
        void runSave();
      }
    }
  }, [enabled]);

  // Debounced save function
  const debouncedSave = useCallback(() => {
    if (!enabled) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      void runSave();
    }, debounceMs);
  }, [debounceMs, enabled, runSave]);

  // Immediate save (for blur, unload, cart changes, section complete)
  const immediateSave = useCallback(async () => {
    if (!enabled) return;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    await runSave();
  }, [enabled, runSave]);

  // Save on page visibility change (user switches tabs/windows)
  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        void immediateSave();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, immediateSave]);

  // Save on beforeunload (user closes/refreshes page)
  useEffect(() => {
    if (!enabled) return;

    const handleBeforeUnload = () => {
      void immediateSave();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, immediateSave]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    debouncedSave,
    immediateSave,
  };
}
